import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export interface EventNoteAttachmentRow extends AttachmentVoidFields {
  id: number;
  event_note_id: number;
  original_file_name: string;
  stored_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface EventNoteRow {
  id: number;
  event_id: number;
  user_id: number | null;
  submitted_by: string | null;
  note: string;
  created_at: string;
  updated_at: string;
  is_pinned: boolean;
  pinned_at: string | null;
  pinned_by_user_id: number | null;
  pinned_by: string | null;
  attachments?: EventNoteAttachmentRow[];
}

/** Pinned notes first (most recently pinned on top), then oldest to newest. */
export function sortEventNotes(notes: EventNoteRow[]): EventNoteRow[] {
  return [...notes].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
    if (a.is_pinned && b.is_pinned) {
      const diff = new Date(b.pinned_at ?? 0).getTime() - new Date(a.pinned_at ?? 0).getTime();
      if (diff !== 0) return diff;
    }
    const created = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return created !== 0 ? created : a.id - b.id;
  });
}

async function parse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchEventNotes(eventId: number): Promise<EventNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes`);
  const data = await parse<{ notes?: EventNoteRow[] }>(res, 'Failed to load notes.');
  return data.notes ?? [];
}

export async function createEventNote(
  eventId: number,
  payload: { note: string; user_id: number }
): Promise<EventNoteRow> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parse<EventNoteRow>(res, 'Failed to add note.');
}

export async function updateEventNote(
  eventId: number,
  noteId: number,
  payload: { note: string; user_id: number }
): Promise<EventNoteRow> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return parse<EventNoteRow>(res, 'Failed to update note.');
}

/** Pins or unpins a note; open to anyone who can see the event. */
export async function setEventNotePinned(
  eventId: number,
  noteId: number,
  payload: { pinned: boolean; user_id: number }
): Promise<EventNoteRow> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(noteId)}/pin`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return parse<EventNoteRow>(res, 'Failed to update the pin.');
}

export async function deleteEventNote(eventId: number, noteId: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(
      noteId
    )}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  await parse<{ success?: boolean }>(res, 'Failed to delete note.');
}

export async function uploadEventNoteAttachment(
  eventId: number,
  noteId: number,
  file: File,
  userId: number
): Promise<EventNoteAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  form.append('user_id', String(userId));
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(noteId)}/attachments`,
    { method: 'POST', body: form }
  );
  return parse<EventNoteAttachmentRow>(res, 'Failed to upload file.');
}

export function eventNoteAttachmentDownloadUrl(eventId: number, noteId: number, attachmentId: number): string {
  return `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(
    noteId
  )}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

function eventNoteAttachmentUrl(eventId: number, noteId: number, attachmentId: number): string {
  return `${API_BASE}/api/events/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(
    noteId
  )}/attachments/${encodeURIComponent(attachmentId)}`;
}

/** Sets a file aside with a reason. The file stays on the note and can be restored. */
export function voidEventNoteAttachment(
  eventId: number,
  noteId: number,
  attachmentId: number,
  reason: string,
  userId: number
): Promise<EventNoteAttachmentRow> {
  return patchAttachmentVoid(eventNoteAttachmentUrl(eventId, noteId, attachmentId), reason, userId);
}

export function unvoidEventNoteAttachment(
  eventId: number,
  noteId: number,
  attachmentId: number,
  userId: number
): Promise<EventNoteAttachmentRow> {
  return patchAttachmentVoid(eventNoteAttachmentUrl(eventId, noteId, attachmentId), null, userId);
}
